<template>
  <div class="cta_report">
    <h3>
      <span class="title">{{ $t('cta.rankList.title') }} {{ getClicksLabel }}</span>
      <ui-icon-tooltip arrowCenter arrowTop iconSize="normal" class="tooltip">
        <span v-html="$t('cta.rankList.tooltip')"></span>
      </ui-icon-tooltip>
    </h3>
    <ReportSegment v-if="reportPeriod.start === reportPeriod.end && planData.planNumber === 8"
                  :timeSeries="timeSeries"
                  @onDeviceDataByHour="onDeviceDataByHour"/>
    <!-- no data -->
    <div v-if="!hasData" class="no-data">
      <p v-html="$t('reporting.click.noClickData')"></p>
    </div>
    <div v-else>
      <div class="cta_report__rank-list">
        <ul>
          <top-rank-item v-for="(item, index) in topData" :key="index"
            :imageData="imageData"
            :item="item"
            :index="index"
            :isDesktopDevice="isDesktopDevice"
            :reportPeriod="reportPeriod"
            :applyData="applyData"
            @onThumbnailClick="highlightCoords"
            @onCtaCoordsClick="onCtaCoordsClick"/>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { getPercentage } from '@/libs/utils'
import TopRankItem from './TopRankItem'
import UiIconTooltip from '@/components/ui/UiIconTooltip'
import ReportSegment from '@/components/reporting-heatmaps/ReportSegment'
import { mapState } from 'vuex'
import { NEW_MOBILE_HEATMAP_STARTDATE } from '@/libs/constants'

export default {
  name: 'CtaRankList',
  components: {
    TopRankItem,
    UiIconTooltip,
    ReportSegment
  },
  props: [
    'reportName',
    'reportData',
    'hasTopData',
    'activeSelector',
    'ctaData',
    'isLoading',
    'timeSeries'
  ],
  data () {
    return {
      hourData: null
    }
  },
  computed: {
    ...mapState([
      'reportPeriod',
      'applyData',
      'planData'
    ]),
    getClicksLabel () {
      let bAMP = this.applyData.bAMP
      let bNewMobile = true
      if ((this.reportPeriod.start < NEW_MOBILE_HEATMAP_STARTDATE) || bAMP || this.isDesktopDevice) {
        bNewMobile = false
      }

      return bNewMobile ? 'Taps' : 'Clicks'
    },
    imageData () {
      return this.reportData.image
    },
    isDesktopDevice () {
      return this.reportData.device === 'desktop'
    },
    topData () {
      let items = []
      const rawData = this.reportData.data

      if (!rawData) {
        return items
      }

      const totalClick = rawData.totalClick
      items = rawData.topClicks.reduce((pre, item) => {
        if (rawData.coords[item.dom]) {
          pre.push({
            value: item.clickCnt || item.tabCnt,
            coords: rawData.coords[item.dom],
            clickPercentage: getPercentage(item.clickCnt || item.tabCnt, totalClick)
          })
        }
        return pre
      }, []).sort((a, b) => b.value - a.value)

      return items
    },
    hasData () {
      this.$emit('update:hasTopData', this.topData.length)
      return this.topData.length
    }
  },
  created () {
  },
  methods: {
    highlightCoords (item) {
      let selector = item.coords.selector

      if (selector === this.activeSelector) {
        selector = null
      }

      this.$emit('onActiveSelectorChange', selector)
    },
    onCtaCoordsClick (coords) {
      const ctaData = { ...this.ctaData }
      ctaData.coords = {
        height: coords.height,
        left: coords.left,
        selector: coords.selector,
        top: coords.top,
        width: coords.width
      }
      this.$emit('onCtaDataChange', ctaData)
    },
    onDeviceDataByHour (value) {
      this.$emit('onHourData', value)
    }
  },
  destroyed () {
    this.$emit('onActiveSelectorChange', null)
  }
}
</script>

<style lang="scss">
  .cta_report {
    width: 100%;
    h3 {
      font-size: 22px;
      font-weight: 500;
      color: #444444;

    span {
      margin-right: 6px;
    }

    .tooltip {
      vertical-align: middle;
    }
  }

  .cta_report__type {
    margin-top: 30px;

    li {
      display: inline-block;
      font-size: 15px;
      font-weight: 500;
      color: #999999;
      padding-bottom: 8px;

      &:nth-last-of-type(1) {
        margin-left: 36px;
      }

      &.on {
        color: #2f2f2f;
        border-bottom: 3px solid $brandColor;
      }
    }
  }

  .cta_report__rank-summary {
    margin-top: 20px;

    .cta_report__rank-summary-title {
      .title {
        font-size: 15px;
        font-weight: 500;
        color: #2f2f2f;
      }

      .more-info {
        float: right;
        cursor: pointer;
        i {
          margin-right: 4px;
        }
        span {
          font-weight: bold;
          color: #444444;
          line-height: 13px;
        }
      }
    }

    .notification {
      font-size: 12px;
      color: $brandColor;
      margin-top: 20px;
    }

    .reporting__click__rank-summary-chart {
      margin-top: 14px;
    }
  }

  .reporting__click__rank-list {
    .reporting__click__rank-list-item{
      margin-top: 30px;

      strong {
        font-size: 15px;
        .rank {
          color: #2f2f2f;
        }
        .value {
          color: $brandColor;
        }
      }

      .rank-list-item__element-info {
        margin-top: 13px;
        display: flex;
        justify-content: space-between;
        height: 75px;
        border-radius: 6px;
        border: solid 1px #d7d7d7;
        background-color: #f4f4f4;
        color: #666666;
        cursor: pointer;

        .rank-list-item__element-img {
          position: relative;
          width: calc(100% - 145px);

          .element-img--wrap {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
          }
        }

        .rank-list-item__element-detail {
          position: relative;
          width: 145px;

          ul {
            width: 100%;
            position: absolute;
            top: 50%;
            right: 50%;
            transform: translate(50%, -50%);
            font-size: 12px;
            line-height: 1.5;
          }
        }

        .hover_mark {
          position: absolute;
          top: 50%;
          right: 50%;
          transform: translate(50%, -50%);
          display: none;
        }

        &:hover {
          background-color: rgba(0, 0, 0, 0.7);
          color: #fff;

          .hover_mark {
            display: block;
          }

          .element-img--wrap::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.7);
          }
        }
      }

      .rank-list-item__element-analysis{
        margin-top: 13px;

        ul{
          float: left;
          width: 50%;

          li {
            margin-top: 12px;
          }

          p {
            display: inline-block;
            vertical-align: top;
            font-size: 13px;
            width: calc(100% - 20px); /* 20px = icon's width */
            min-width: 123px;
            max-width: 175px;

            .title {
              margin-left: 4px;
              color: #666666;
            }

            .value {
              color: #2f2f2f;
              float: right;
            }
          }
        }
      }

      .rank-list-item__element-chart {
        margin-top: 18px;
      }
    }
  }

  .btn__back-to-top {
    cursor: pointer;
    width: 100px;
    margin: 40px auto 0;
    text-align: center;

    p {
      margin-top: 8px;
    }
  }
}

</style>
